import {
  Button,
  DatePicker,
  Flex,
  Popconfirm,
  Select,
  Table,
  TableProps,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { debounce } from "lodash";
import React, { useState } from "react";
import { LeaveType, StepStatus } from "../../enum/common";
import { ILeaveRequest, ILeaveRequestQuery } from "../../models/Hrm";
import { IPaginatedResponse } from "../../models/Pagination";
import { useCancelLeaveRequest } from "../../query/HrmQuery";
import { notify } from "../../utils/toast";
import { getStatusTag, getTypeTag } from "../filter/FilterEnum";
import { getDisplayStatus } from "../filter/FilterUserOperation";
import { ModalAddLeaveRequest } from "../modal/hrm/ModalAddLeaveRequest";

const { Text } = Typography;
const { RangePicker } = DatePicker;
interface props {
  data: IPaginatedResponse<ILeaveRequest>;
  isLoading: boolean;
  filter: ILeaveRequestQuery;
  setFilter: React.Dispatch<React.SetStateAction<ILeaveRequestQuery>>;
}
export const PersonalLeaveRequestTable: React.FC<props> = ({
  data,
  filter,
  isLoading,
  setFilter,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const calculateTotalDays = (startDate: Date, endDate: Date) => {
    const days =
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    return Math.ceil(days * 2) / 2;
  };
  const cancel = useCancelLeaveRequest();
  const handleCancel = async (id: string) => {
    if (!id) return;
    try {
      await cancel.mutateAsync({ id });
      notify.success("Hủy thành công");
    } catch (error) {
      notify.error("Hủy thất bại");
    }
  };
  const statusApproval = [
    { value: StepStatus.HR_PENDING, label: "Chờ duyệt" },
    { value: StepStatus.APPROVED, label: "Đã duyệt" },
    { value: StepStatus.REJECTED, label: "Từ chối" },
  ];
  const debouncedSearch = React.useMemo(
    () =>
      debounce((field: keyof ILeaveRequestQuery, value?: string) => {
        setFilter((prev) => ({
          ...prev,
          [field]: value || undefined,
          page: 1,
        }));
      }, 300),
    [setFilter],
  );

  const handleSearch =
    (field: keyof ILeaveRequestQuery) => (value?: string) => {
      debouncedSearch(field, value);
    };

  const columns: TableProps<ILeaveRequest>["columns"] = [
    {
      title: "Thời gian",
      key: "time",
      width: 250,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <RangePicker
          showTime
          format="DD/MM/YYYY HH:mm"
          onChange={(dates) => {
            setFilter((prev) => ({
              ...prev,
              start: dates?.[0]?.format("YYYY-MM-DD HH:mm"),
              end: dates?.[1]?.format("YYYY-MM-DD HH:mm"),
            }));
            confirm?.();
          }}
        />
      ),
      render: (_, record) => (
        <Text strong>
          {dayjs(record.startDate).format("DD/MM/YYYY HH:mm")}
          {" -> "}
          {dayjs(record.endDate).format("DD/MM/YYYY HH:mm")}
        </Text>
      ),
    },

    {
      title: "Số ngày",
      dataIndex: "totalDays",
      key: "totalDays",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Text strong>
          {calculateTotalDays(
            new Date(record.startDate),
            new Date(record.endDate),
          )}
        </Text>
      ),
    },
    {
      title: "Loại nghỉ",
      dataIndex: "type",
      key: "type",
      width: 150,
      filteredValue: filter.type ? [filter.type] : null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <Select
          value={filter.type}
          placeholder="Chọn trạng thái"
          className="w-full"
          onChange={(value) => {
            const v = value ? String(value) : undefined;
            setSelectedKeys(v ? [v] : []);
            confirm?.();
            handleSearch("type")(v);
          }}
          options={[
            { value: LeaveType.EARLY, label: "Sớm" },
            { value: LeaveType.SUDDENTY, label: "Đột xuất" },
            { value: LeaveType.PLAN, label: "Có kế hoạch" },
          ]}
          allowClear
        />
      ),
      render: (type) => (
        <Tag
          color={getTypeTag(type).color}
          className="px-2 py-1 rounded-xl font-semibold"
        >
          {getTypeTag(type).label}
        </Tag>
      ),
    },

    {
      title: filter.status
        ? `Trạng thái (${getStatusTag(filter.status).label})`
        : "Trạng thái",
      width: 180,
      filteredValue: filter.status ? [filter.status] : null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <Select
          className="w-[150px]"
          placeholder="Chọn..."
          value={filter.status}
          onChange={(value) => handleSearch("status")(value)}
          allowClear
          options={statusApproval}
        />
      ),
      render: (_, record) => {
        const statusDisplay = getDisplayStatus(record);
        return (
          <Tag
            color={statusDisplay.color}
            className="px-2 py-1 rounded-xl font-semibold"
          >
            {statusDisplay.label}
          </Tag>
        );
      },
    },
    {
      title: "Lý do",
      dataIndex: "detail",
      key: "detail",
      ellipsis: true,
      width: 100,
    },
    {
      title: "Thao tác",
      key: "action",
      align: "center",
      width: 150,
      render: (_, record) => {
        const isCanCancel = record.status?.includes("PENDING");

        return (
          <Popconfirm
            title="Hủy đơn nghỉ?"
            description="Bạn có chắc muốn hủy đơn nghỉ phép này?"
            okText="Hủy"
            cancelText="Đóng"
            onConfirm={() => handleCancel(record.id)}
            disabled={!isCanCancel}
          >
            <Button type="primary" disabled={!isCanCancel}>
              Hủy đơn
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div className="flex-1  p-4 overflow-auto h-full">
      <Flex vertical gap="large" className="w-full">
        <Table
          scroll={{ x: 1000 }}
          columns={columns}
          dataSource={data?.data}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: data?.pagination.page,
            pageSize: data?.pagination.limit,
            total: data?.pagination.total,
            onChange: (page, pageSize) => {
              setFilter({
                ...filter,
                page: page,
                limit: pageSize,
              });
            },
          }}
          className="shadow-sm border border-gray-100 overflow-hidden"
          onRow={(record) => ({
            className: " hover:bg-blue-50/30",
          })}
        />
        <ModalAddLeaveRequest
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          query={filter}
        />
      </Flex>
    </div>
  );
};

export default PersonalLeaveRequestTable;
